import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../lib/api';
import './TechStackTab.css';

// Sentinel for the catch-all tile. A Symbol would be tidier, but this key is
// compared against `tech.category` values and put in React keys, so a string
// that cannot collide with a real category is the simpler choice.
const UNCATEGORISED = '__uncategorised__';

function TechStackTab({ accountId, onNavigate }) {
  const [technologies, setTechnologies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [modalMode, setModalMode] = useState('view'); // 'view', 'edit', 'add'
  const [editingTech, setEditingTech] = useState(null);
  const [formData, setFormData] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingTech, setDeletingTech] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  // P0.21 task 21.10b. The tenant's derived attribute set, or null when it has
  // not been configured — in which case the packaged default below renders and
  // the panel behaves exactly as it did before 21.10.
  const [shape, setShape] = useState(null);

  // ── THE PACKAGED DEFAULT (21.10b) ──────────────────────────────────────────
  //
  // What a tenant sees when it has NOT been derived for. These eight categories
  // plus their legacy subcategory tiles are what every tenant saw before 21.10,
  // so an unconfigured tenant's panel is byte-for-byte what it was — the same
  // property that made migration 0014 safe to apply.
  //
  // NOT the same axis as the category dropdown, deliberately: 'Underwriting' and
  // 'Portal' are SUBCATEGORIES (13 and 48 live rows; zero as a category), so
  // their tiles match on that column instead. That is P&C-shaped and it is
  // exactly why this is now a FALLBACK rather than the product's only answer.
  //
  // UNCATEGORISED IS LOAD-BEARING (21.5b). getTechsByCategory used to end in a
  // bare `return false`, so a row matching no tile rendered nowhere at all —
  // 13 of 430 live rows, five of them 'Payments', which the dropdown below
  // offers and which had no tile. A user could save a technology and never see
  // it again. A catch-all tile means an unrecognised value is visible and
  // fixable rather than lost.
  const DEFAULT_TILES = [
    { key: 'Core', label: 'Core System', icon: '🏢' },
    { key: 'Underwriting', label: 'Underwriting Workbench', icon: '📋' },
    { key: 'Integration', label: 'API Management', icon: '🔗' },
    { key: 'Portal', label: 'Portal', icon: '🌐' },
    { key: 'CRM', label: 'CRM', icon: '👥' },
    { key: 'Doc Man', label: 'Doc Management', icon: '📄' },
    { key: 'Analytics', label: 'Analytics & Reporting', icon: '📊' },
    { key: 'Data', label: 'Database', icon: '💾' },
    { key: 'AI/ML', label: 'AI/ML', icon: '🤖' },
    { key: 'Payments', label: 'Payments', icon: '💳' },
  ];

  // An icon per tile, because a derived set has no icons: the model returns a
  // key, a label and a hint, and asking it for an emoji invites a different one
  // on every re-derivation. Assigned by POSITION so the grid stays visually
  // varied whatever the tenant's groups are called.
  const DERIVED_ICONS = ['🏢', '📊', '🔗', '📋', '💾', '🤖', '🌐', '💳'];

  // The tiles actually rendered: the tenant's derived set when it has one, the
  // packaged default otherwise. Uncategorised is appended to BOTH — a row whose
  // group no longer exists must stay visible, and that matters more for a
  // derived set than for the default, because a re-derivation can retire a key.
  const attributes = shape?.groups?.length ? shape : null;
  const TECH_CATEGORIES = [
    ...(attributes
      ? attributes.groups.map((g, i) => ({
          key: g.key,
          label: g.label,
          icon: DERIVED_ICONS[i % DERIVED_ICONS.length],
          hint: g.hint || null,
        }))
      : DEFAULT_TILES),
    { key: UNCATEGORISED, label: 'Uncategorised', icon: '❓' },
  ];

  // What the panel and its button are called. A commercial-space broker should
  // never see a tab called "Tech Stack" or a button reading "Add Technology".
  const panelLabel = attributes?.panel_label || 'Technology Stack';
  const itemLabel = attributes?.item_label || 'Technology';

  // Dropdown options.
  // CATEGORIES and SUBCATEGORIES must stay in step with VOCAB.techCategory and
  // VOCAB.techSubcategory in api/shared/import-schema.js. The duplication is
  // structural — the CRA build cannot import from api/ — and
  // tests/tech-stack-vocabulary.test.js asserts the two sides are identical,
  // because they had already drifted three ways before anyone noticed.
  const CATEGORIES = ['Core', 'Digital', 'Analytics', 'AI/ML', 'Integration', 'Data', 'Doc Man', 'Payments'];
  const SUBCATEGORIES = ['Policy', 'Claims', 'Billing', 'Underwriting', 'Portal', 'MDM'];
  const VENDORS = ['Guidewire', 'Duckcreek', 'Salesforce', 'DXC', 'Origami', 'InsuranceNow', 'Majesco', 'Socotra', 'Hi Marley', 'Snowflake', 'Microsoft Dynamics'];
  const STATUSES = ['Live', 'Implementing', 'Planned', 'Retiring'];
  const DEPLOYMENTS = ['Cloud', 'On Prem', 'Hybrid'];
  const CONFIDENCE_LEVELS = ['Confirmed', 'Inferred'];

  const fetchTechStack = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiFetch(`/api/tech-stack/by-account?accountId=${accountId}`);
      const data = await response.json();

      if (data.success) {
        setTechnologies(data.technologies || []);
        // Travels with the rows, so the tiles and their contents render in the
        // same frame. Null is the normal state for an unconfigured tenant.
        setShape(data.attributes || null);
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError('Failed to load technology stack');
      console.error('Tech stack error:', err);
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    fetchTechStack();
  }, [fetchTechStack]);

  /**
   * Does this row belong in the tile named by `categoryKey`?
   *
   * TWO MATCHERS, and keeping them apart is the point of this task.
   *
   * A DERIVED tile matches on `category` equality and nothing else. The special
   * cases below are P&C-shaped history — a subcategory axis and a Digital/Portal
   * alias — and inheriting them would mean a freight forwarder's "Trade Lanes"
   * tile silently claiming rows whose subcategory happened to read 'Portal'.
   * A derived key is exact or it is nothing.
   *
   * The DEFAULT tiles keep every special case byte-for-byte, because 429 of the
   * 430 live rows belong to one tenant that has not been derived for yet. Note
   * 'Portal' claims category 'Digital' as well as subcategory 'Portal' — that is
   * where the 117 live 'Digital' rows appear, and changing it would relocate a
   * quarter of the data with no migration.
   */
  const matchesTile = (tech, categoryKey) => {
    const techCategory = tech.category || '';
    const techSubcategory = tech.subcategory || '';

    if (attributes) return techCategory === categoryKey;

    if (categoryKey === 'Core' && techCategory === 'Core') return true;
    if (categoryKey === 'Underwriting' && techSubcategory === 'Underwriting') return true;
    if (categoryKey === 'Integration' && techCategory === 'Integration') return true;
    if (categoryKey === 'Portal' && (techCategory === 'Digital' || techSubcategory === 'Portal')) return true;
    if (categoryKey === 'CRM' && techCategory === 'CRM') return true;
    if (categoryKey === 'Doc Man' && techCategory === 'Doc Man') return true;
    if (categoryKey === 'Analytics' && techCategory === 'Analytics') return true;
    if (categoryKey === 'Data' && techCategory === 'Data') return true;
    if (categoryKey === 'AI/ML' && techCategory === 'AI/ML') return true;
    if (categoryKey === 'Payments' && techCategory === 'Payments') return true;

    return false;
  };

  // The named tiles, so the catch-all below can ask "did anything claim this?"
  // rather than repeating the list and drifting from it.
  const NAMED_TILES = TECH_CATEGORIES
    .map(c => c.key)
    .filter(k => k !== UNCATEGORISED);

  // Group technologies by tile. Every row lands in at least one: anything no
  // named tile claims falls to Uncategorised, which is what stops a row from
  // vanishing when its category is unrecognised, null, or newly added to the
  // vocabulary before a tile exists for it.
  const getTechsByCategory = (categoryKey) => {
    if (categoryKey === UNCATEGORISED) {
      return technologies.filter(t => !NAMED_TILES.some(k => matchesTile(t, k)));
    }
    return technologies.filter(t => matchesTile(t, categoryKey));
  };

  const handleCategoryClick = (categoryKey) => {
    setSelectedCategory(categoryKey);
    setModalMode('view');
  };

  const handleCloseModal = () => {
    setSelectedCategory(null);
    setModalMode('view');
    setEditingTech(null);
    setFormData({});
    setSuccessMessage('');
  };

  const handleEditClick = (tech) => {
    setEditingTech(tech);
    setFormData({
      technology: tech.technology || '',
      category: tech.category || '',
      subcategory: tech.subcategory || '',
      vendor: tech.vendor || '',
      status: tech.status || '',
      deployment: tech.deployment || '',
      confidence_level: tech.confidence_level || '',
      notes: tech.notes || '',
    });
    setModalMode('edit');
  };

  /**
   * What to prefill the category field with when adding from inside a tile.
   *
   * 'Portal' is a tile, not a category, so it prefills 'Digital' — the value
   * that actually lands a row back in that tile. Uncategorised prefills
   * nothing: it is a destination for rows that failed to match, never a
   * category to assign, and stamping the sentinel into the column would write
   * the very kind of junk value this task removed.
   */
  const prefillCategory = () => {
    if (selectedCategory === 'Portal') return 'Digital';
    if (selectedCategory === UNCATEGORISED) return '';
    return selectedCategory;
  };

  const handleAddClick = () => {
    setFormData({
      technology: '',
      category: prefillCategory(),
      subcategory: '',
      vendor: '',
      status: 'Planned',
      deployment: 'Cloud',
      confidence_level: 'Confirmed',
      notes: '',
    });
    setModalMode('add');
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      if (modalMode === 'edit') {
        // Update existing
        const response = await apiFetch('/api/tech-stack/update', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: editingTech.id,
            properties: formData,
          }),
        });

        const data = await response.json();
        if (data.success) {
          setSuccessMessage('✅ Technology updated successfully!');
          await fetchTechStack();
          setTimeout(() => {
            setModalMode('view');
            setEditingTech(null);
            setSuccessMessage('');
          }, 1500);
        } else {
          alert('Error: ' + data.error);
        }
      } else if (modalMode === 'add') {
        // Create new
        const response = await apiFetch('/api/tech-stack/create', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            accountId,
            properties: formData,
          }),
        });

        const data = await response.json();
        if (data.success) {
          setSuccessMessage('✅ Technology created successfully!');
          await fetchTechStack();
          
          // Clear form but STAY in add mode
          setFormData({
            technology: '',
            category: prefillCategory(),
            subcategory: '',
            vendor: '',
            status: 'Planned',
            deployment: 'Cloud',
            confidence_level: 'Confirmed',
            notes: '',
          });
          
          setTimeout(() => {
            setSuccessMessage('');
          }, 2000);
        } else {
          alert('Error: ' + data.error);
        }
      }
    } catch (err) {
      alert('Error saving: ' + err.message);
    }
  };

  const handleDeleteClick = (tech) => {
    setDeletingTech(tech);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await apiFetch('/api/tech-stack/delete', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: deletingTech.id,
        }),
      });

      const data = await response.json();
      if (data.success) {
        setSuccessMessage('✅ Technology deleted successfully!');
        await fetchTechStack();
        setShowDeleteConfirm(false);
        setDeletingTech(null);
        setTimeout(() => {
          setSuccessMessage('');
          // If no more techs in this category, close modal
          const remainingTechs = getTechsByCategory(selectedCategory).filter(t => t.id !== deletingTech.id);
          if (remainingTechs.length === 0) {
            handleCloseModal();
          }
        }, 1500);
      } else {
        alert('Error: ' + data.error);
      }
    } catch (err) {
      alert('Error deleting: ' + err.message);
    }
  };

  if (loading) {
    return <div className="tech-stack-loading">Loading technology stack...</div>;
  }

  if (error) {
    return <div className="tech-stack-error">Error: {error}</div>;
  }

  const selectedCategoryData = selectedCategory 
    ? TECH_CATEGORIES.find(cat => cat.key === selectedCategory)
    : null;
  const selectedTechs = selectedCategory 
    ? getTechsByCategory(selectedCategory)
    : [];

  return (
    <div className="tech-stack-container">
      <div className="tech-stack-header">
        <h3>{panelLabel}</h3>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="success-toast">{successMessage}</div>
      )}

      {/* Grid of category cards. Uncategorised is hidden when empty — it is a
          safety net, and an always-visible "No data / Click to add" tile would
          invite adding a technology with no category, which is the state it
          exists to surface rather than to encourage. */}
      {/* NOT CONFIGURED, AND NOTHING RECORDED YET (21.10b, feeding 21.10e).
          Shown only when BOTH are true: a tenant with rows already has a working
          panel and does not need telling, and a configured tenant with no rows
          just has an empty grid, which is correct.

          Concrete examples rather than instructions, deliberately. "Market you
          sell into" is abstract; naming what an IT firm and a broker each get
          shows what a good answer looks like. The readiness banner in Settings
          is the other half of this and is exactly where the user ISN'T when they
          hit this screen. */}
      {!attributes && technologies.length === 0 && !loading && (
        <div className="tech-not-configured">
          <p className="tnc-lead">
            DigitalMe doesn&apos;t yet know what to research about your accounts.
          </p>
          <p className="tnc-body">
            Tell us what you sell and who you sell to, and it works out the rest —
            for an IT firm that&apos;s core systems and integrations; for a
            real-estate broker, lease expiries and floor space.
          </p>
          {/* This app has no router — Dashboard.jsx is a view switcher, and
              'profile' is the key ProfileSettingsPage renders under. A plain
              href would reload the SPA and lose the account context. */}
          {onNavigate ? (
            <button className="tnc-link" onClick={() => onNavigate('profile')}>
              Complete your company profile →
            </button>
          ) : (
            <span className="tnc-link-plain">
              Complete your company profile in Profile &amp; Settings.
            </span>
          )}
        </div>
      )}

      <div className="tech-categories-grid">
        {TECH_CATEGORIES.map((category) => {
          const categoryTechs = getTechsByCategory(category.key);
          const hasData = categoryTechs.length > 0;
          if (category.key === UNCATEGORISED && !hasData) return null;

          return (
            <div
              key={category.key}
              className={`tech-category-card ${hasData ? 'has-data' : 'empty'}`}
              onClick={() => handleCategoryClick(category.key)}
            >
              <div className="category-icon">{category.icon}</div>
              <h4 className="category-name">{category.label}</h4>
              
              {hasData ? (
                <div className="category-summary">
                  {categoryTechs.map((tech, idx) => (
                    <div key={idx} className="tech-summary-item">
                      <div className="tech-summary-name">{tech.technology}</div>
                      <div className="tech-summary-details">
                        {tech.subcategory && <span>{tech.subcategory}</span>}
                        {tech.deployment && <span>{tech.deployment}</span>}
                        {tech.vendor && <span>{tech.vendor}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="category-empty">
                  <p>No data</p>
                  <span className="add-hint">Click to add</span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Modal for viewing/editing category details */}
      {selectedCategory && (
        <div className="tech-modal-overlay" onClick={modalMode === 'view' ? handleCloseModal : undefined}>
          <div className="tech-modal" onClick={(e) => e.stopPropagation()}>
            <div className="tech-modal-header">
              <h3>
                {selectedCategoryData.icon} {selectedCategoryData.label}
              </h3>
              <button className="close-btn" onClick={handleCloseModal}>×</button>
            </div>

            <div className="tech-modal-content">
              {modalMode === 'view' && (
                <>
                  {selectedTechs.length > 0 ? (
                    <div className="tech-details-list">
                      {selectedTechs.map((tech) => (
                        <div key={tech.id} className="tech-detail-card">
                          <div className="tech-detail-header">
                            <h4>{tech.technology}</h4>
                            {tech.status && (
                              <span className={`status-badge status-${tech.status.toLowerCase().replace(' ', '-')}`}>
                                {tech.status}
                              </span>
                            )}
                          </div>
                          
                          <div className="tech-detail-info">
                            {tech.subcategory && (
                              <div className="tech-info-row">
                                <span className="tech-info-label">Type:</span>
                                <span className="tech-info-value">{tech.subcategory}</span>
                              </div>
                            )}
                            {tech.vendor && (
                              <div className="tech-info-row">
                                <span className="tech-info-label">Vendor:</span>
                                <span className="tech-info-value">{tech.vendor}</span>
                              </div>
                            )}
                            {tech.deployment && (
                              <div className="tech-info-row">
                                <span className="tech-info-label">Deployment:</span>
                                <span className="tech-info-value">{tech.deployment}</span>
                              </div>
                            )}
                            {tech.confidence_level && (
                              <div className="tech-info-row">
                                <span className="tech-info-label">Confidence:</span>
                                <span className="tech-info-value">{tech.confidence_level}</span>
                              </div>
                            )}
                            {tech.notes && (
                              <div className="tech-info-row">
                                <span className="tech-info-label">Notes:</span>
                                <span className="tech-info-value">{tech.notes}</span>
                              </div>
                            )}
                          </div>

                          <div className="tech-detail-actions">
                            <button className="btn-edit" onClick={() => handleEditClick(tech)}>Edit</button>
                            <button className="btn-delete" onClick={() => handleDeleteClick(tech)}>Delete</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="tech-modal-empty">
                      <p>No technologies in this category yet.</p>
                    </div>
                  )}
                </>
              )}

              {(modalMode === 'edit' || modalMode === 'add') && (
                <div className="tech-form">
                  <div className="form-group">
                    <label>{itemLabel} Name *</label>
                    <input
                      type="text"
                      value={formData.technology || ''}
                      onChange={(e) => handleFormChange('technology', e.target.value)}
                      placeholder={attributes ? '' : 'e.g., Guidewire PolicyCenter'}
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Category</label>
                      <select
                        value={formData.category || ''}
                        onChange={(e) => handleFormChange('category', e.target.value)}
                      >
                        <option value="">-</option>
                        {/* The tenant's own groups when derived, the packaged
                            vocabulary otherwise. Offering the eight IT
                            categories to a freight forwarder is how a row ends
                            up in a tile that tenant has never heard of. */}
                        {(attributes
                          ? attributes.groups.map(g => [g.key, g.label])
                          : CATEGORIES.map(c => [c, c])
                        ).map(([value, label]) => (
                          <option key={value} value={value}>{label}</option>
                        ))}
                      </select>
                    </div>

                    {/* Subcategory is a LEGACY AXIS and is hidden for a derived
                        tenant. Its six values are P&C policy administration
                        terms, 175 of the 177 live values came from the Notion
                        migration, and a derived set has no second axis at all —
                        offering "Policy|Claims|Billing" to a staffing agency is
                        worse than offering nothing. */}
                    {!attributes && (
                      <div className="form-group">
                        <label>Subcategory</label>
                        <select
                          value={formData.subcategory || ''}
                          onChange={(e) => handleFormChange('subcategory', e.target.value)}
                        >
                          <option value="">-</option>
                          {SUBCATEGORIES.map(sub => (
                            <option key={sub} value={sub}>{sub}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Vendor</label>
                      {/* A FIXED LIST OF ELEVEN INSURANCE VENDORS. Usable for a
                          tenant the default was written for, useless for anyone
                          else — a freight forwarder cannot record a landlord or
                          a carrier from a Guidewire/Duck Creek dropdown, and a
                          select with no matching option renders blank.
                          A derived tenant gets free text until 21.10c derives a
                          vendor list too; the column is free text either way. */}
                      {attributes ? (
                        <input
                          type="text"
                          value={formData.vendor || ''}
                          onChange={(e) => handleFormChange('vendor', e.target.value)}
                        />
                      ) : (
                        <select
                          value={formData.vendor || ''}
                          onChange={(e) => handleFormChange('vendor', e.target.value)}
                        >
                          <option value="">-</option>
                          {VENDORS.map(vendor => (
                            <option key={vendor} value={vendor}>{vendor}</option>
                          ))}
                        </select>
                      )}
                    </div>

                    <div className="form-group">
                      <label>Status</label>
                      <select
                        value={formData.status || ''}
                        onChange={(e) => handleFormChange('status', e.target.value)}
                      >
                        <option value="">-</option>
                        {STATUSES.map(status => (
                          <option key={status} value={status}>{status}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Deployment</label>
                      <select
                        value={formData.deployment || ''}
                        onChange={(e) => handleFormChange('deployment', e.target.value)}
                      >
                        <option value="">-</option>
                        {DEPLOYMENTS.map(dep => (
                          <option key={dep} value={dep}>{dep}</option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Confidence</label>
                      <select
                        value={formData.confidence_level || ''}
                        onChange={(e) => handleFormChange('confidence_level', e.target.value)}
                      >
                        <option value="">-</option>
                        {CONFIDENCE_LEVELS.map(conf => (
                          <option key={conf} value={conf}>{conf}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Notes</label>
                    <textarea
                      value={formData.notes || ''}
                      onChange={(e) => handleFormChange('notes', e.target.value)}
                      rows={3}
                      placeholder="Additional information..."
                    />
                  </div>

                  <div className="form-actions">
                    <button className="btn-cancel" onClick={() => setModalMode('view')}>
                      {modalMode === 'add' ? 'Done Adding' : 'Cancel'}
                    </button>
                    <button className="btn-save" onClick={handleSave}>
                      {modalMode === 'edit' ? 'Save Changes' : 'Create & Add Another'}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {modalMode === 'view' && (
              <div className="tech-modal-footer">
                <button className="btn-add-tech" onClick={handleAddClick}>
                  + Add New {itemLabel}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="tech-modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="delete-confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Delete Technology?</h3>
            <p>Are you sure you want to delete <strong>{deletingTech?.technology}</strong>?</p>
            <p className="delete-warning">This action cannot be undone.</p>
            <div className="delete-actions">
              <button className="btn-cancel" onClick={() => setShowDeleteConfirm(false)}>Cancel</button>
              <button className="btn-delete-confirm" onClick={handleDeleteConfirm}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TechStackTab;
